import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { api, type AufgabeFelder, type MeineAufgabe } from '../api';
import {
  MEIN_FILTER_LEER, aufgabeInfo, datumKurz, eintragen, faelligkeit, heuteIso, istBeteiligt, matrix, matrixAenderung, meineDarfSchreiben, meinFilterAktiv, meinFilterAusSuche, meinFilterSuche, meineGruppieren, meinePasst, meineProjekte, quadrant, nrText,
  type MeinFilter, type Quadrant,
} from '../aufgaben';
import { Choice, TextFilter, type Option } from './fields';
import { useIch } from '../App';
import '../styles/beteiligte.css';
import { toast } from './toast';
import { useNachladen } from './nachladen';
import { menueOeffnen, adresse, kopieren, neuerTab } from './Kontextmenue';
import { aufgabeMenue } from '../kontextmenue';
import { useAufgabeInfo } from './AufgabeInfo';
import { MerkStern } from './MerkStern';

const NACHLADEN_MS = 15_000;
const FAELLIG_OPTIONEN: Option[] = [
  { value: '', label: 'Alle Termine' }, { value: 'ueberfaellig', label: 'Überfällig' }, { value: 'heute', label: 'Heute fällig' },
  { value: 'woche', label: 'Fällig in 7 Tagen' }, { value: 'ohne', label: 'Ohne Datum' },
];

/**
 * „Meine Aufgaben“ auf der Startseite: Aufgaben, für die ich verantwortlich bin, an denen ich beteiligt bin oder die ich mir
 * gemerkt habe (Stern), aus allen Projekten, gruppiert nach Fälligkeit. Filter (Text, Projekt, Fälligkeit, erledigte ein/aus,
 * nur verantwortlich, nur gemerkte) stehen in der Adresse (?q=…&projekt=…&faellig=…&erledigte=1&nurzustaendig=1&gemerkt=1).
 * Abhaken je Projekt erst ab mitwirkend (archivierte nur lesbar); merken darf jede Rolle.
 * Statt nach Fälligkeit lassen sie sich als Eisenhower-Matrix zeigen (?ansicht=matrix; wichtig = Priorität Hoch, dringend = bis 7 Tage).
 * In der Matrix ordnet Ziehen eine Aufgabe in jeden anderen Quadranten ein (wie im Projekt): Priorität und/oder Fälligkeit ändern sich.
 */
export function MeineAufgaben() {
  const { search } = useLocation();
  const nav = useNavigate();
  const { ich } = useIch();
  const email = ich?.email.toLowerCase() ?? '';
  // Gerade abgehakte bleiben (durchgestrichen) stehen, bis die Seite neu geladen wird oder der Filter wechselt
  const [behalten, setBehalten] = useState<Set<string>>(new Set());
  // Filter sofort im Zustand, zusätzlich in der Adresse (ersetzt den Verlaufseintrag), damit Links ihn behalten
  const [filter, setFilterRoh] = useState<MeinFilter>(() => meinFilterAusSuche(search));
  const setFilter = (f: MeinFilter) => {
    setFilterRoh(f);
    setBehalten(new Set());
    const s = meinFilterSuche(f, search);
    nav({ search: s ? '?' + s : '' }, { replace: true });
  };
  // Adresse von außen geändert (Link, Zurück): übernehmen
  useEffect(() => {
    const f = meinFilterAusSuche(search);
    setFilterRoh((alt) => (meinFilterSuche(alt) === meinFilterSuche(f) ? alt : f));
  }, [search]);
  const alsMatrix = new URLSearchParams(search).get('ansicht') === 'matrix';
  const setAlsMatrix = (an: boolean) => {
    const q = new URLSearchParams(search);
    if (an) q.set('ansicht', 'matrix'); else q.delete('ansicht');
    const s = q.toString();
    nav({ search: s ? '?' + s : '' }, { replace: true });
  };
  const [liste, setListe] = useState<MeineAufgabe[] | null>(null);
  const [fehler, setFehler] = useState('');
  const heute = heuteIso();
  // Infotext beim Überfahren; Vorgänger, Unteraufgaben und Abschnitt kennt die Startseite nicht – sie fallen weg bzw. stehen als Anzahl da
  const { info, element: infoElement } = useAufgabeInfo();
  const personName = (z: string) => (ich && z.toLowerCase() === email ? (ich.name || ich.email) + ' (Sie)' : z);
  useEffect(() => {
    api.meineAufgaben(filter.erledigte).then(setListe).catch((e) => setFehler((e as Error).message));
  }, [filter.erledigte]);
  // Änderungen anderer nachladen; was hier gerade abgehakt oder umsortiert wurde, bleibt stehen, auch wenn der Server es nicht mehr liefert
  const aenderungen = useRef(0); // verwirft Nachlade-Ergebnisse, die eigene Änderungen überholen würden
  const behaltenRef = useRef(behalten);
  behaltenRef.current = behalten;
  useNachladen(() => {
    const vorher = aenderungen.current;
    api.meineAufgaben(filter.erledigte).then((neu) => {
      if (aenderungen.current !== vorher) return;
      const da = new Set(neu.map((a) => a.id));
      setListe((alt) => [...neu, ...(alt ?? []).filter((a) => behaltenRef.current.has(a.id) && !da.has(a.id))]);
    }).catch(() => { /* Nachladen versucht es erneut */ });
  }, NACHLADEN_MS, [filter.erledigte]);

  const abhaken = async (a: MeineAufgabe) => {
    setBehalten((b) => new Set(b).add(a.id));
    setListe((l) => l && (eintragen(l, { ...a, erledigt: !a.erledigt }) as MeineAufgabe[]));
    aenderungen.current++;
    try {
      const r = await api.aufgabeAendern(a.projektId, a.id, a.zeile, { erledigt: !a.erledigt });
      aenderungen.current++;
      setListe((l) => l && (eintragen(l, { ...a, ...r.aufgabe }) as MeineAufgabe[]));
    } catch (e) {
      toast('Nicht gespeichert: ' + (e as Error).message, true);
      setListe((l) => l && (eintragen(l, a) as MeineAufgabe[]));
    }
  };

  // Matrix: Ziehen in einen anderen Quadranten setzt Priorität (wichtig ↔ Hoch) und/oder Fälligkeit (dringend ↔ heute fällig)
  const [ziehe, setZiehe] = useState<MeineAufgabe | null>(null);
  const [ziel, setZiel] = useState<Quadrant | null>(null);
  const zieheEnde = () => { setZiehe(null); setZiel(null); };
  const einordnen = async (a: MeineAufgabe, felder: AufgabeFelder) => {
    // bleibt stehen, auch wenn sie nach der Änderung nicht mehr zum Fälligkeitsfilter passt
    setBehalten((b) => new Set(b).add(a.id));
    setListe((l) => l && (eintragen(l, { ...a, ...felder }) as MeineAufgabe[]));
    aenderungen.current++;
    try {
      const r = await api.aufgabeAendern(a.projektId, a.id, a.zeile, felder);
      aenderungen.current++;
      setListe((l) => l && (eintragen(l, { ...a, ...r.aufgabe }) as MeineAufgabe[]));
    } catch (e) {
      toast('Nicht gespeichert: ' + (e as Error).message, true);
      setListe((l) => l && (eintragen(l, a) as MeineAufgabe[]));
    }
  };

  // Stern umschalten; ein entfernter Stern lässt die Aufgabe bis zum Neuladen stehen (sonst spränge die Zeile weg)
  const merken = async (a: MeineAufgabe) => {
    const gemerkt = !a.gemerkt;
    const setze = (g: boolean) => setListe((l) => l && l.map((x) => (x.id === a.id ? { ...x, gemerkt: g } : x)));
    setBehalten((b) => new Set(b).add(a.id));
    setze(gemerkt);
    aenderungen.current++;
    try {
      await api.aufgabeMerken(a.projektId, a.id, gemerkt);
      aenderungen.current++;
    } catch (e) {
      toast('Nicht gemerkt: ' + (e as Error).message, true);
      setze(!gemerkt);
    }
  };

  // Eine Zeile (in den Fälligkeitsgruppen wie in der Matrix)
  const zeile = (a: MeineAufgabe) => {
    const darfSchreiben = meineDarfSchreiben(ich, a.projektId);
    return (
      <Link key={a.id} to={`/p/${encodeURIComponent(a.projektId)}/aufgaben/${encodeURIComponent(a.id)}`}
        className={'aufgabe-zeile' + (a.erledigt ? ' erledigt' : '') + (a.id === ziehe?.id ? ' zieht' : '')} data-testid={'meine-aufgabe-' + a.id}
        draggable={alsMatrix && darfSchreiben ? true : undefined}
        onDragStart={alsMatrix && darfSchreiben ? (e) => { e.dataTransfer.setData('text/plain', a.id); e.dataTransfer.effectAllowed = 'move'; setZiehe(a); } : undefined}
        onDragEnd={alsMatrix ? zieheEnde : undefined}
        {...info(() => aufgabeInfo(a, { heute, personName, projekt: `${a.kundeName} · ${a.projektTitel}` }))}
        onContextMenu={(e) => {
          const pfad = `/p/${encodeURIComponent(a.projektId)}/aufgaben/${encodeURIComponent(a.id)}`;
          menueOeffnen(e, aufgabeMenue(a, { darfSchreiben, darfLoeschen: false }, {
            oeffnen: () => nav(pfad),
            neuerTab: () => neuerTab(adresse(pfad)),
            abhaken: () => abhaken(a),
            merken: () => merken(a),
            linkKopieren: () => kopieren(adresse(pfad)),
          }, heute));
        }}>
        <input type="checkbox" className="haken" checked={a.erledigt} disabled={!darfSchreiben}
          title={!darfSchreiben ? 'Nur lesbar' : a.erledigt ? 'Wieder öffnen' : 'Als erledigt markieren'}
          onClick={(e) => e.stopPropagation()} onChange={() => void abhaken(a)} data-testid="meine-haken" />
        <span className="lfd-nr" title="ID der Aufgabe">{nrText(a.nr)}</span>
        <span className="titel">{a.titel}</span>
        <MerkStern gemerkt={a.gemerkt} onUmschalten={() => void merken(a)} />
        {email && (a.zustaendig ?? '').toLowerCase() !== email && istBeteiligt({ zustaendig: null, beteiligte: a.beteiligte }, email) && <span className="chip beteiligt-chip" title="Sie sind an dieser Aufgabe beteiligt, verantwortlich ist jemand anderes" data-testid="meine-beteiligt">beteiligt</span>}
        <span className="wo" title={`${a.kundeName} · ${a.projektTitel}`}>{a.kundeName} · {a.projektTitel}</span>
        {a.faellig && <span className={'faellig ' + (a.erledigt ? '' : faelligkeit(a.faellig, heute))}>{datumKurz(a.faellig, heute)}</span>}
      </Link>
    );
  };

  if (fehler) return null; // Startseite bleibt nutzbar, auch wenn die Aufgaben gerade nicht laden
  const aktiv = meinFilterAktiv(filter);
  const gefiltert = liste ? liste.filter((a) => meinePasst(a, filter, heute, behalten, email)) : [];
  const gruppen = meineGruppieren(gefiltert, heute);
  const felder = alsMatrix ? matrix(gefiltert, heute) : [];
  const nimmt = (q: (typeof felder)[number]) => !!ziehe && quadrant(ziehe, heute) !== q.key;
  const projekte = liste ? meineProjekte(liste) : [];
  const projektOptionen: Option[] = [{ value: '', label: 'Alle Projekte' }, ...projekte.map((p) => ({ value: p.id, label: p.label }))];
  // gewähltes Projekt ohne (sichtbare) Aufgaben trotzdem benennen können
  if (filter.projekt && !projekte.some((p) => p.id === filter.projekt)) projektOptionen.push({ value: filter.projekt, label: filter.projekt });
  const zeigeFilter = !!liste && (liste.length > 0 || aktiv);
  return (
    <section className="meine-aufgaben" data-testid="meine-aufgaben">
      <div className="kopf">
        <h2 style={{ flex: 'none' }}>Meine Aufgaben{liste && liste.length > 0 && <span className="hint" style={{ fontWeight: 400, marginLeft: 8 }}>{liste.filter((a) => !a.erledigt).length} offen</span>}</h2>
        <span style={{ flex: 1 }} />
        {liste && liste.length > 0 && (
          <div className="umschalter" role="group" aria-label="Ansicht">
            <button className={'btn small' + (alsMatrix ? '' : ' on')} onClick={() => setAlsMatrix(false)} data-testid="meine-ansicht-liste">Nach Fälligkeit</button>
            <button className={'btn small' + (alsMatrix ? ' on' : '')} onClick={() => setAlsMatrix(true)} data-testid="meine-ansicht-matrix"
              title="Eisenhower-Matrix: wichtig (Priorität Hoch) und dringend (in 7 Tagen fällig)">Matrix</button>
          </div>
        )}
      </div>
      {zeigeFilter && (
        <div className="meine-filter" data-testid="meine-filter">
          <TextFilter wert={filter.text} onChange={(text) => setFilter({ ...filter, text })} testid="meine-filter-text" />
          {projekte.length > 1 || filter.projekt ? (
            <Choice value={filter.projekt} options={projektOptionen} onChange={(v) => setFilter({ ...filter, projekt: v ?? '' })} testid="meine-filter-projekt" />
          ) : null}
          <Choice value={filter.faellig} options={FAELLIG_OPTIONEN} onChange={(v) => setFilter({ ...filter, faellig: (v ?? '') as MeinFilter['faellig'] })} testid="meine-filter-faellig" />
          <label className="check" title="Erledigte Aufgaben mit anzeigen">
            <input type="checkbox" checked={filter.erledigte} onChange={(e) => setFilter({ ...filter, erledigte: e.target.checked })} data-testid="meine-filter-erledigte" />
            Erledigte anzeigen
          </label>
          <label className="check" title="Nur Aufgaben, für die ich verantwortlich bin – ohne die, an denen ich nur beteiligt bin">
            <input type="checkbox" checked={filter.nurZustaendig} onChange={(e) => setFilter({ ...filter, nurZustaendig: e.target.checked })} data-testid="meine-filter-zustaendig" />
            Nur verantwortlich
          </label>
          <label className="check" title="Nur Aufgaben, die Sie sich mit dem Stern gemerkt haben">
            <input type="checkbox" checked={filter.nurGemerkt} onChange={(e) => setFilter({ ...filter, nurGemerkt: e.target.checked })} data-testid="meine-filter-gemerkt" />
            Nur gemerkte
          </label>
          {aktiv && <button className="btn small" onClick={() => setFilter(MEIN_FILTER_LEER)} data-testid="meine-filter-zuruecksetzen">Filter zurücksetzen</button>}
        </div>
      )}
      {!liste && <div className="hint">Wird geladen …</div>}
      {liste && liste.length === 0 && !aktiv && <div className="hint" data-testid="meine-aufgaben-leer">Sie sind gerade für keine offenen Aufgaben verantwortlich, daran beteiligt oder haben sich welche gemerkt.</div>}
      {liste && gruppen.length === 0 && (liste.length > 0 || aktiv) && <div className="hint" data-testid="meine-aufgaben-kein-treffer">Keine Aufgaben zum Filter.</div>}
      {alsMatrix && gefiltert.length > 0 && (
        <div className="matrix meine-matrix" data-testid="meine-matrix">
          {felder.map((q) => (
            <section key={q.key} className={'matrix-feld q-' + q.key + (ziel === q.key ? ' ziel' : '')} data-testid={'meine-quadrant-' + q.key}
              onDragOver={(e) => { if (!nimmt(q)) return; e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (ziel !== q.key) setZiel(q.key); }}
              onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setZiel(null); }}
              onDrop={(e) => {
                e.preventDefault();
                const a = ziehe;
                zieheEnde();
                const neu = a && nimmt(q) ? matrixAenderung(a, q, heute) : undefined;
                if (a && neu) void einordnen(a, neu);
              }}>
              <h2>{q.titel} <span className="anzahl">{q.aufgaben.length}</span><span className="hint">{q.hinweis}</span></h2>
              {q.aufgaben.map(zeile)}
              {q.aufgaben.length === 0 && <div className="hint leer">Keine Aufgaben.</div>}
            </section>
          ))}
        </div>
      )}
      {!alsMatrix && gruppen.map((g) => (
        <div key={g.art} data-testid={'meine-' + g.art}>
          <h3 className={g.art}>{g.titel} <span className="hint">({g.aufgaben.length})</span></h3>
          {g.aufgaben.map(zeile)}
        </div>
      ))}
      {infoElement}
    </section>
  );
}
